/**
 * Where to send someone after they open the email-confirmation link, and what it unlocked.
 * Kept in localStorage because the link usually opens in a new tab.
 */
const KEY = 'ResponseDock-verify-return';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface VerificationReturn {
  path: string;
  unlocked: string | null;
  savedAt: number;
}

function isSafePath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/verify-email') && !path.startsWith('/login') && !path.startsWith('/register');
}

export function rememberVerificationReturn(path: string, unlocked: string | null) {
  if (!isSafePath(path)) return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ path, unlocked, savedAt: Date.now() } satisfies VerificationReturn));
  } catch {
    // Storage unavailable: the confirmation page falls back to /getting-started.
  }
}

export function takeVerificationReturn(now = Date.now()): VerificationReturn | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    window.localStorage.removeItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<VerificationReturn>;
    if (typeof parsed.path !== 'string' || !isSafePath(parsed.path)) return null;
    if (typeof parsed.savedAt !== 'number' || now - parsed.savedAt > MAX_AGE_MS) return null;
    return { path: parsed.path, unlocked: typeof parsed.unlocked === 'string' ? parsed.unlocked : null, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

/** What an unverified account unlocks by confirming its email (the API's verification-gated features). */
export const VERIFICATION_UNLOCKS = 'API keys, webhooks, email invitations, Zapier and chat notifications';

/** Navigation state that AppLayout turns into a success toast. */
export interface FlashState {
  flash?: string;
}

/** "Email confirmed. Webhooks are unlocked." */
export function verifiedMessage(unlocked: string | null): string {
  const what = unlocked && unlocked !== 'this feature' ? unlocked : VERIFICATION_UNLOCKS;
  const plural = what === VERIFICATION_UNLOCKS || /s$/.test(what);
  return `Email confirmed. ${what.charAt(0).toUpperCase()}${what.slice(1)} ${plural ? 'are' : 'is'} unlocked.`;
}
